import round from 'lodash/round';
import moment from 'moment';

import { getRollingDataForInterval, portfolioHasMonthlyData, portfolioHoldingsToComparisonHoldings } from './utils';
import calculatePortfolioRiskMetrics from './risk-metrics';
import calculatePerformance from './performance';
import {
  getAssetAllocations,
  getSectorAllocations,
  getCountryAllocations,
  getHoldingAllocations,
  getAllocations,
  getHoldingTypes,
  getLocalizedAllocations,
  aggregateAssetDetails,
  getFundAllocations,
} from './allocation';
import { uniqueArray } from '../objects';
import { CURRENCY } from '../currency';
import { altToInvestmentItem } from '../alts';
import { fundToInvestmentItem } from '../funds';
import { stockToInvestmentItem } from '../stocks';
import {
  weightedAverage,
  harmonicWeightedAverage,
  getCommonTimestamps,
  getDollarsAmountFromUnits,
  getLatestPriceForHolding,
  getUnitsFromDollarsAmount,
  initRiskData,
  weightedAverageRating,
} from '../calculation-utils';
import { HOLDING_TYPES, PortfolioData, setDefaultAllocationMode } from '../portfolios';
import { calculateHoldingWeightsFromDollars, calculateTotalSizeOfHoldings } from '@capintel/instruments-lib';
import { HoldingAllocationMode } from '@/enums/Portfolio';
import { CREDIT_QUALITY_RATINGS, Interval } from '../constants';
import {
  getESGCarbonIntensity,
  getESGImpliedTemperatureRise,
  getESGRatingAllocations,
  getESGRatingClassification,
  getESGRatingValue,
  getESGScore,
} from '../calculation-esg-utils';
import Dictionary from '@/types/dictionary';
import Portfolio from '@/types/portfolio';
import Holding from '@/types/holding';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Benchmark from '@/types/benchmark';
import Nullable from '@/types/nullable';
import InvestmentItem from '@/types/investment-item';
import PortfolioHolding from '@/types/portfolio-holding';
import Timestamp from '@/types/timestamp';
import PortfolioOverrideDates from '@/types/portfolio-override-dates';
import PortfolioRisk from '@/types/portfolio-risk';
import Allocations from '@/types/allocations';
import CustomAllocation from '@/types/custom-allocation';
import CommonInception from '@/types/common-inception';
import Alt from '@/types/models/alt';
import { calculateAltMetrics } from '../alts/calculation';

const calculatePortfolioMer = (portfolio: Portfolio, data: Holding[]) => {
  const mer = weightedAverage(portfolio, data, 'mer', false);
  return mer ? round(mer, 2) : undefined;
};

/**
 * Get portfolio dates
 * @param {Holding[]} holdings the portfolio holdings
 * @param {Fund[]} funds the portfolio funds
 * @param {Stock[]} stocks the portfolio stocks
 * @param {Benchmark[]} benchmarks the portfolio benchmarks
 * @param {Portfolio} portfolio the Portfolio
 */
const getPortfolioDates = (holdings: Holding[], funds: Fund[], stocks: Stock[], benchmarks: Benchmark[], portfolio?: Portfolio) => {
  let endDate: Nullable<number> = null;
  if (portfolioHasMonthlyData([...holdings, ...benchmarks] as Holding[])) {
    const lastCommonTimestamp = getCommonTimestamps(
      [...holdings, ...benchmarks].map(item => item.performance.rolling),
      'DESC',
    ).monthly;
    endDate = lastCommonTimestamp;
  }
  const dates = {
    allocations: {} as Dictionary<Nullable<Date>>,
    fixedIncomeSector: {} as Dictionary<Nullable<Date>>,
    holdings: {} as Dictionary<Nullable<Date>>,
    growth: {} as Dictionary<Nullable<Date>>,
    risk: {
      lastCalculated: new Date(),
      endDate: endDate ? new Date(endDate) : null,
    },
    investmentInfo: {} as Dictionary<Nullable<Date>>,
    characteristics: {} as Dictionary<Nullable<Date>>,
    yieldToMaturity: {} as Dictionary<Nullable<Date>>,
    esgLatestHolding: {} as Dictionary<Nullable<Date>>,
    ...(portfolio?.dates?.savedAt ? { savedAt: portfolio.dates.savedAt } : {}),
  };

  funds.forEach(fund => {
    dates.allocations[fund._id] = fund.dates.allocations ? fund.dates.allocations : null;
    dates.fixedIncomeSector[fund._id] = fund.dates.fixedIncomeSector ? fund.dates.fixedIncomeSector : null;
    dates.holdings[fund._id] = fund.dates.holdings ? fund.dates.holdings : null;
    dates.characteristics[fund._id] = fund.dates.characteristics ? fund.dates.characteristics : null;
    dates.yieldToMaturity[fund._id] = fund.dates.yieldToMaturity ? fund.dates.yieldToMaturity : null;
    dates.esgLatestHolding[fund._id] = fund.dates.esgLatestHolding ? fund.dates.esgLatestHolding : null;
  });

  const hasClosedHoldings = [...stocks, ...funds].some(h => !!h.inactive);
  if (!hasClosedHoldings) {
    [...stocks, ...funds].forEach(h => {
      dates.growth[h._id] = h.dates.growth ? h.dates.growth : null;
      dates.investmentInfo[h._id] = h.dates.investmentInfo ? h.dates.investmentInfo : null;
    });
  }

  return dates;
};

/**
 * Refresh portfolio currency
 * @param holdings Portfolio holdings
 * @param data Funds and stocks data
 */
const refreshPortfolioCurrency = (portfolio: Portfolio, data: (Fund | Stock | Alt)[]) => {
  // Refresh holdings currency
  const holdings = portfolio.holdings.map(h => {
    const item = data.find(d => d._id === h.original.id);

    return {
      ...h,
      currency: item && item.currency,
    };
  });

  // Evaluate new portfolio currency
  const portfolioCurrencies = uniqueArray(holdings.filter(h => h.weight > 0).map(h => h.currency));
  const currency = portfolioCurrencies.length === 1 ? portfolioCurrencies[0] : CURRENCY.MIXED;

  return {
    ...portfolio,
    currency,
    holdings,
  };
};

type PortfolioAssetType = 'equity' | 'fixedIncome';

const getAllocationByType = (holding: PortfolioHolding, investmentItems: InvestmentItem[], type: PortfolioAssetType) => {
  const dataItem = investmentItems.find(item => item._id === holding.original.id);
  if (!dataItem || !dataItem.allocations || !dataItem.allocations.asset) {
    return 0;
  }

  const aggregated = aggregateAssetDetails(dataItem.allocations.asset);
  return aggregated[type] / 100;
};

const getPortfolioByAssetType = (portfolio: Portfolio, investmentItems: InvestmentItem[], type: PortfolioAssetType): Portfolio => {
  const holdingsTotal = portfolio.holdings.reduce((sum, holding: PortfolioHolding) => {
    const allocation = getAllocationByType(holding, investmentItems, type);
    return sum + allocation * holding.weight;
  }, 0);

  const holdings = portfolio.holdings
    .map(holding => {
      const allocation = getAllocationByType(holding, investmentItems, type);
      return {
        ...holding,
        weight: allocation === 0 ? 0 : (holding.weight * allocation) / holdingsTotal,
        dollars: holding.dollars * allocation,
      };
    })
    .filter(holding => holding.weight > 0);

  const size = (portfolio.size || 1) * (holdingsTotal / 100);

  return {
    ...portfolio,
    size,
    holdings,
  };
};

/**
 * Recalculates the dollars and weights of a portfolio's holdings from the set units, based on the latest prices.
 * Also recalculates the portfolio's new size.
 *
 * @param portfolio Portfolio to recalculate.
 * @param data Most recent data for the portfolio's holdings.
 */
export const recalculateHoldingAmountsFromUnits = (portfolio: Portfolio, data: Holding[]): Portfolio => {
  // First, recalculate the dollars amount from the latest price of each respective holding.
  let newHoldings: PortfolioHolding[] = portfolio.holdings.map(holding => {
    const holdingData = data.find(h => h._id === holding.original.id);

    if (!holdingData || !getLatestPriceForHolding(holding.original.type, holdingData)) {
      return holding;
    }

    return {
      ...holding,
      dollars: getDollarsAmountFromUnits(holding.original.type, holding.units || 0, holdingData) || 0,
    };
  });

  // Then recalculate the portfolio's total size
  const size = calculateTotalSizeOfHoldings(newHoldings);

  // And finally, calculate the weight from the new
  newHoldings = calculateHoldingWeightsFromDollars(newHoldings, size);

  return {
    ...portfolio,
    size,
    holdings: newHoldings,
  };
};

/**
 * Recalculates the units of a portfolio's holdings from the set dollars, based on the latest prices.
 *
 * @param portfolio Portfolio to recalculate.
 * @param data Most recent data for the portfolio's holdings.
 */
export const recalculateHoldingUnitsFromDollars = (portfolio: Portfolio, data: Holding[]): Portfolio => {
  // Recalculate the units of each individual holding
  const newHoldings: PortfolioHolding[] = portfolio.holdings.map(holding => {
    const holdingData = data.find(h => h._id === holding.original.id);

    if (!holdingData || !getLatestPriceForHolding(holding.original.type, holdingData)) {
      return holding;
    }

    return {
      ...holding,
      units: getUnitsFromDollarsAmount(holding.original.type, holding.dollars || 0, holdingData),
    };
  });

  return {
    ...portfolio,
    holdings: newHoldings,
  };
};

/**
 * Recalculates the allocations of a portfolio based on the latest prices.
 * If portfolio was built using units, recalculate the dollars and weights as of today.
 * Otherwise, recalculate the units from the dollars.
 *
 * @param portfolio Portfolio to recalculate.
 * @param data Most recent data for the portfolio's holdings.
 */
export const recalculatePortfolioAllocations = (portfolio: Portfolio, data: Holding[]): Portfolio => {
  if (portfolio.allocationMode === HoldingAllocationMode.Units) {
    // Recalculate weights and dollars
    return recalculateHoldingAmountsFromUnits(portfolio, data);
  }
  return recalculateHoldingUnitsFromDollars(portfolio, data);
};

/** Reallocate and recalculate a portfolio
 *
 * @param portfolio Portfolio to calculate
 * @param data Most recent data for the portfolio's holdings
 * @param taxRate Tax rate
 */
export const reallocateAndRecalculatePortfolio = (
  portfolio: Portfolio,
  data: PortfolioData,
  forcedMonthlyRolling: boolean,
  taxRate: number = 0,
  commonInception?: CommonInception,
) => {
  const { funds, stocks, benchmarks, alts } = data;

  let newPortfolio = setDefaultAllocationMode(portfolio);
  newPortfolio = recalculatePortfolioAllocations(newPortfolio, [...funds, ...stocks]);

  // portfolio get recalculated right after with proper currency information.
  // TODO, removed this as soon as we are sure we do not need any of it.
  // Update: This line is still needed as it populates the allocations field for portfolio, which recalcPortfolioItem, often called afterwards, does not do
  newPortfolio = calculatePortfolioStats({
    portfolio: newPortfolio,
    alts,
    funds,
    stocks,
    benchmarks,
    taxRate,
    showBaseCurrency: false,
    forcedMonthlyRolling,
    shortHistory: false,
    commonInception,
  });

  return newPortfolio;
};

/**
 * Aggregates the custom data provided for underlying holdings of the portfolio.
 * This custom data currently only support custom "allocations" (key-value pairs),
 * and on funds only.
 *
 * @param portfolio Portfolio to recalculate.
 * @param data Most recent data for the portfolio's holdings.
 */
export const aggregatePortfolioCustomData = (portfolio: Portfolio, data: Holding[]) => {
  const fundHoldings = portfolio.holdings.filter(holding => holding.original.type === HOLDING_TYPES.FUND);

  const allocations = fundHoldings.reduce((acc, holding) => {
    const fundData = data.find(d => d._id === holding.original.id) as Nullable<Fund>;
    const customAllocations = fundData?.customData?.allocations;

    if (customAllocations == null) {
      return acc;
    }

    const customAllocationsDict = customAllocations as Dictionary<CustomAllocation>;

    Object.keys(customAllocationsDict).forEach(allocationKey => {
      if (!acc[allocationKey]) {
        acc[allocationKey] = {
          asAt: customAllocationsDict[allocationKey].asAt,
          provider: customAllocationsDict[allocationKey].provider,
          data: [],
          // Key subject to change
          primary: customAllocationsDict[allocationKey].primary,
        };
      }

      customAllocationsDict[allocationKey].data.forEach(newAlloc => {
        const existingAllocIndex = acc[allocationKey].data.findIndex(alloc => alloc.key === newAlloc.key);
        const weightedValue = (newAlloc.value * holding.weight) / 100;

        if (existingAllocIndex === -1) {
          acc[allocationKey].data.push({
            key: newAlloc.key,
            value: weightedValue,
          });
        } else {
          acc[allocationKey].data[existingAllocIndex].value += weightedValue;
        }
      });
    });

    return acc;
  }, {} as Dictionary<CustomAllocation>);

  return {
    allocations,
  };
};

/**
 * Calculate new values for portfolio
 * @param {CalculatePortfolioStatsOptions} options - Object containing all parameters for portfolio calculation
 * @param {Portfolio} options.portfolio - Portfolio to be calculated
 * @param {Alt[]} options.funds - Alts data for the portfolio alts
 * @param {Fund[]} options.funds - Funds data for the portfolio funds
 * @param {Stock[]} options.stocks - Stocks data for the portfolio stocks
 * @param {Benchmark[]} options.benchmarks - Benchmarks data for comparison
 * @param {number} options.taxRate - Tax rate value to use in calculations
 * @param {boolean} options.showBaseCurrency - Indicates if the base currency of the holding should be shown
 * @param {boolean} options.forcedMonthlyRolling - Whether to use monthly rolling data
 * @param {boolean} [options.shortHistory] - Indicates if portfolio should be calculated with short history
 * @param {CommonInception} [options.commonInception] - CommonInception data to calculate stats
 * @return {Portfolio} Portfolio with new calculations
 */
interface CalculatePortfolioStatsOptions {
  portfolio: Portfolio;
  alts: Alt[];
  funds: Fund[];
  stocks: Stock[];
  benchmarks: Benchmark[];
  taxRate: number;
  showBaseCurrency: boolean;
  forcedMonthlyRolling: boolean;
  shortHistory?: boolean;
  commonInception?: CommonInception;
}

const calculatePortfolioStats = ({
  portfolio,
  alts,
  funds,
  stocks,
  benchmarks,
  taxRate,
  showBaseCurrency,
  forcedMonthlyRolling,
  shortHistory,
  commonInception,
}: CalculatePortfolioStatsOptions): Portfolio => {
  if (!portfolio.holdings) {
    return portfolio;
  }

  const data = [...alts, ...funds, ...stocks].filter(f => portfolio.holdings.some(holding => holding.original.id === f._id));
  const investmentItems = [...alts.map(altToInvestmentItem), ...stocks.map(stockToInvestmentItem), ...funds.map(fundToInvestmentItem)];
  const holdings = portfolioHoldingsToComparisonHoldings(portfolio.holdings, data);
  const fees = (portfolio.discretionaryMer || 0) * (1 + taxRate);

  const monthlyData = getRollingDataForInterval(portfolio, holdings, Interval.Monthly, fees, shortHistory);
  let monthlyRolling: [Timestamp, number][] = monthlyData.rolling;

  const portfolioOverrideDates = portfolio.override?.dates != null ? (portfolio.override.dates as PortfolioOverrideDates) : null;
  if (portfolioOverrideDates?.inceptionDate != null) {
    monthlyRolling = monthlyRolling.filter(rolling => rolling[0] > moment.utc(portfolioOverrideDates.inceptionDate).toDate().getTime());
  }

  const performance = calculatePerformance(portfolio, data, monthlyRolling, taxRate, forcedMonthlyRolling, shortHistory);

  const hasMonthly = portfolioHasMonthlyData(data);
  const risk = hasMonthly
    ? calculatePortfolioRiskMetrics(portfolio, monthlyRolling, data, benchmarks, showBaseCurrency, monthlyData.override, shortHistory, commonInception)
    : (initRiskData(true) as PortfolioRisk);

  portfolio = refreshPortfolioCurrency(portfolio, data);

  // Create type-specific versions of the portfolio - used for equity / fixed income metrics.
  // These are identical to the original portfolio, except that the weight of the holdings
  // are adjusted to reflect only the portion of the type specified.
  // ie: Fund-A 60% weight with 50% equity => Fund-A 30% weight for equity portfolio.
  const equityPortfolio = getPortfolioByAssetType(portfolio, investmentItems, 'equity');
  const fixedIncomePortfolio = getPortfolioByAssetType(portfolio, investmentItems, 'fixedIncome');

  const allocations: Allocations = {
    // Global portfolio allocations
    asset: getAssetAllocations(portfolio, investmentItems),
    country: getCountryAllocations(portfolio, investmentItems, 'allocations.country'),
    holdingTypes: getHoldingTypes(portfolio, investmentItems),
    region: getAllocations(portfolio, investmentItems, 'region'),
    // Equity portfolio allocations
    sector: getSectorAllocations(equityPortfolio, investmentItems),
    equityCountry: getAllocations(equityPortfolio, investmentItems, 'equityCountry'),
    equityRegion: getAllocations(equityPortfolio, investmentItems, 'equityRegion'),
    // Fixed Income portfolio allocations
    creditQuality: getAllocations(fixedIncomePortfolio, investmentItems, 'creditQuality'),
    maturity: getLocalizedAllocations(fixedIncomePortfolio, investmentItems, 'allocations.maturity'),
    fixedIncomeRegion: getAllocations(fixedIncomePortfolio, investmentItems, 'fixedIncomeRegion'),
    fixedIncomeSector: getLocalizedAllocations(fixedIncomePortfolio, investmentItems, 'allocations.fixedIncomeSector'),
    fixedIncomeCountry: getAllocations(fixedIncomePortfolio, investmentItems, 'fixedIncomeCountry'),
  };

  const investmentInfo = {
    // Equity portfolio metrics
    style: getLocalizedAllocations(equityPortfolio, investmentItems, 'style'),
    capitalization: getLocalizedAllocations(equityPortfolio, investmentItems, 'capitalization'),
    // In a portfolio, if there is a holding that does not have a return on equity, we should return undefined
    returnOnEquity: investmentItems.every(investmentItem => investmentItem.returnOnEquity !== undefined)
      ? weightedAverage(equityPortfolio, investmentItems, 'returnOnEquity')
      : undefined,
    // Distribution Yield is not asset type specific
    distributionYield: weightedAverage(portfolio, investmentItems, 'distributionYield'),
    pbRatio: harmonicWeightedAverage(equityPortfolio, investmentItems, 'pbRatio'),
    peRatio: harmonicWeightedAverage(equityPortfolio, investmentItems, 'peRatio'),
    // Fixed Income portfolio metrics
    creditRatingClass: getLocalizedAllocations(fixedIncomePortfolio, investmentItems, 'creditRatingClass'),
    durationClass: getLocalizedAllocations(fixedIncomePortfolio, investmentItems, 'durationClass'),
    modifiedDuration: weightedAverage(fixedIncomePortfolio, investmentItems, 'modifiedDuration'),
    averageCoupon: weightedAverage(fixedIncomePortfolio, investmentItems, 'averageCoupon'),
    averageQuality: weightedAverageRating(CREDIT_QUALITY_RATINGS, allocations.creditQuality),
    yieldToMaturity: {
      value: weightedAverage(fixedIncomePortfolio, investmentItems, 'yieldToMaturity.value'),
    },
    effectiveMaturity: weightedAverage(fixedIncomePortfolio, investmentItems, 'effectiveMaturity'),
    effectiveDuration: weightedAverage(fixedIncomePortfolio, investmentItems, 'effectiveDuration'),
  };

  const esgScore = getESGScore(portfolio, funds, stocks);
  const esgValue = getESGRatingValue(esgScore);
  const esgClassification = getESGRatingClassification(esgValue);

  const altMetrics = calculateAltMetrics(portfolio, alts);

  return {
    ...portfolio,
    performance: {
      ...performance,
      risk,
    },
    // this is more a placeholder than anything else.
    // the portfolio will get recalculated to recalcPortfolioItem anyway
    // TODO - remove as soon as we can.
    performanceBase: {
      ...performance,
      risk,
    },
    mer: portfolio.override?.mer ?? calculatePortfolioMer(portfolio, data),
    allocations,
    detailedHoldings: {
      list: getHoldingAllocations(portfolio, investmentItems),
      funds: getFundAllocations(portfolio, funds, stocks, alts),
    },
    esg: {
      rating: {
        allocations: getESGRatingAllocations(portfolio, funds, stocks),
        classification: esgClassification,
        score: esgScore,
        value: esgValue,
      },
      carbonIntensity: getESGCarbonIntensity(portfolio, funds, stocks),
      impliedTemperatureRise: getESGImpliedTemperatureRise(portfolio, funds, stocks),
    },
    dates: getPortfolioDates(data, funds, stocks, benchmarks, portfolio),
    investmentInfo,
    customData: aggregatePortfolioCustomData(portfolio, data),
    ...altMetrics,
  } as Portfolio;
};

export default calculatePortfolioStats;
